import { TaskBundle } from 'utils/customTypes';
import {
  PreviewTaskBundleDatesResponse,
  RawPreviewTaskBundleDatesResponse,
} from 'types/store/tasksBundle';

type IncludedUser = {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
};

export type V3BundleResource = {
  id: string;
  attributes: {
    name: string;
    description: string;
    estimatedHoursSum: number | null;
    createdAt?: string;
  };
  relationships?: { creator?: { data?: { id: string } } };
};

export function buildIncludedUsers(
  included: Array<{
    type: string;
    id: string;
    attributes: Record<string, string>;
  }>
): Record<string, IncludedUser> {
  const includedUsers: Record<string, IncludedUser> = {};
  for (const item of included ?? []) {
    if (item.type === 'user') {
      includedUsers[item.id] = {
        firstName: item.attributes.firstName ?? '',
        lastName: item.attributes.lastName ?? '',
        email: item.attributes.email ?? '',
        avatarUrl: item.attributes.avatarUrl ?? '',
      };
    }
  }
  return includedUsers;
}

export function mapV3BundleResourceToTaskBundle(
  resource: V3BundleResource,
  includedUsers: Record<string, IncludedUser>
): TaskBundle {
  const creatorId = resource.relationships?.creator?.data?.id ?? '';
  const creator = includedUsers[creatorId];
  return {
    id: resource.id,
    name: resource.attributes.name,
    description: resource.attributes.description,
    creator_id: creatorId,
    taskBundleCreator: creator
      ? {
          id: creatorId,
          avatar_url: creator.avatarUrl,
          data: {
            email: creator.email,
            firstName: creator.firstName,
            lastName: creator.lastName,
          },
        }
      : null,
    createdAt: resource.attributes.createdAt ?? '',
    totalEstimatedHours:
      resource.attributes.estimatedHoursSum?.toString() ?? '0',
  };
}

export const parseDateStr = (
  dateStr: string
): { year: number; month: number; day: number } => {
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    return { year, month, day };
  }
  const [month, day, year] = dateStr.split('/').map(Number);
  return { year, month, day };
};

export const deserializePreviewTaskBundleDates = (
  response: RawPreviewTaskBundleDatesResponse
): PreviewTaskBundleDatesResponse => ({
  data: response.data.map((item) => ({
    id: item.id,
    name: item.attributes.name,
    startDay: item.attributes.startDay,
    duration: item.attributes.duration,
    calculatedStartDate: item.attributes.calculatedStartDate,
    calculatedEndDate: item.attributes.calculatedEndDate,
  })),
  meta: { hasScheduledTasks: response.meta.hasScheduledTasks },
});
